/**
 * The connection dialog's per-engine draft cache and its three operations (Option C, C0, plan §3.1).
 *
 * - `selectEngine(target)` — the engine tiles (C1: start cards, rail, compact select). A no-op for the current engine.
 *   Otherwise, in one batch: capture the current engine's values, field meta and loaded list; restore the target's draft
 *   unchanged, or seed a first visit (`seedEngineValues`); merge the live shared fields; `reset` the form (which clears
 *   all meta), then put back the target's meta and the shared fields' meta; reconcile validation.
 * - `restoreEngine` is the restore branch of `selectEngine`.
 * - `leaveToStart()` — "All engines" / Escape in the form (C1): captures the current engine's draft and voids pending
 *   work. Until the next pick the form's engine is DETACHED (as it is when the dialog opens on the start view): a pick,
 *   the same engine included, restores that engine's draft or seeds a first visit, and captures nothing.
 * - `applyPatch(target, patch)` — paste, a prefill handoff, a Browse Azure pick (C5: presets). Another engine is selected
 *   first (restore or seed); the current engine is patched in place, with no reset and no draft switch. Each patched
 *   field is written with `setFieldValue` (dirty + touched: a patch is an edit the user asked for); a patch touches the
 *   password only when it carries one, so edit mode's password intent (`passwordEdited`) stays the user's.
 *
 * Every select or patch first calls `onInvalidate` synchronously, before the form is captured or rewritten: a pending
 * Test, Load databases, fix retry or Save then shows nothing and saves nothing (`saveAuthorization.ts`).
 *
 * The cache is a ref — never React state, a store, storage or IPC — so it dies with the dialog.
 */
import { useMemo, useRef, useState } from 'react';
import { batch, useStore } from '@tanstack/react-store';
import type { ConnectionFormValues, DatabaseEngine } from '../../components/connectionFormSchema';
import {
  isNetworkEngine,
  captureScopedMeta,
  captureSharedMeta,
  mergeShared,
  revokeTnsServerProvenance,
  scopedValuesOf,
  seedEngineValues,
  sharedValuesOf,
  targetFingerprint,
  type EngineDraft,
  type LoadedDatabases,
} from './engineDrafts';
import { changedSinceBaseline, enginesWithInput as collectEnginesWithInput, scopedInput, sharedInput, type MetaFlags } from './draftInput';
import { reconcileFieldMeta } from './reconcileFieldMeta';
import type { ConnectionForm } from './connectionForm';
import { saveOriginOf, type EngineSource, type SaveOrigin } from './dialogView';
import { withoutStalePickerProvenance } from './provenance';

export type { EngineSource };

export type EnginePatch = Partial<Omit<ConnectionFormValues, 'engine'>>;

type Options = {
  form: ConnectionForm;
  /** Voids pending work before the form changes; `engineChanged` also starts a new result generation. */
  onInvalidate: (change: { engineChanged: boolean }) => void;
  /** An edit the dialog makes on its own behalf (password hydration): it moves no draft revision. */
  programmatic: (edit: () => void) => void;
  /** The dialog opens on the start view: the form's engine is nobody's draft until the first pick. */
  startsDetached?: boolean;
};

const PRISTINE_META = { isValidating: false, isTouched: false, isBlurred: false, isDirty: false, isPristine: true, errors: [], errorMap: {} };

export function useEngineDrafts({ form, onInvalidate, programmatic, startsDetached = false }: Options) {
  const cache = useRef(new Map<DatabaseEngine, EngineDraft>());
  // How each engine's draft was last filled by a patch (paste, prefill, Azure pick): `feature:connection_saved.origin`.
  const origins = useRef(new Map<DatabaseEngine, EngineSource>());
  const detached = useRef(startsDetached);
  // Moves whenever the cache changes, so the rail's "has input" dots re-render.
  const [cacheVersion, setCacheVersion] = useState(0);
  const liveInput = useStore(form.store, (state) => scopedInput(state.fieldMeta as MetaFlags));
  const liveEngine = useStore(form.store, (state) => state.values.engine);
  const liveSharedInput = useStore(form.store, (state) => sharedInput(state.fieldMeta as MetaFlags));
  const [loaded, setLoadedState] = useState<LoadedDatabases | null>(null);
  const loadedRef = useRef<LoadedDatabases | null>(null);
  const callbacks = useRef({ onInvalidate, programmatic });
  callbacks.current = { onInvalidate, programmatic };
  const liveTarget = useStore(form.store, (state) => targetFingerprint(state.values));
  // A first visit seeded with another network engine's host (final UX review UX-20): which engine it came from, for a
  // one-time note under Host. Any other switch forgets it; an edit of Host hides it.
  const [carried, setCarried] = useState<{ target: DatabaseEngine; from: DatabaseEngine; server: string } | null>(null);
  const liveServer = useStore(form.store, (state) => String(state.values.server ?? ''));
  const serverEdited = useStore(form.store, (state) => (state.fieldMeta as MetaFlags).server?.isDirty === true);

  const setLoaded = (next: LoadedDatabases | null) => {
    loadedRef.current = next;
    setLoadedState(next);
  };

  const captureCurrent = () => {
    const live = form.state.values;
    cache.current.set(live.engine, { values: scopedValuesOf(live), meta: captureScopedMeta(form.state.fieldMeta), ...(loadedRef.current ? { loaded: loadedRef.current } : {}) });
    setCacheVersion((version) => version + 1);
  };

  const switchTo = (target: DatabaseEngine) => {
    const live = form.state.values;
    const meta = form.state.fieldMeta;
    if (!detached.current) captureCurrent();
    detached.current = false;
    setCacheVersion((version) => version + 1);
    const cached = cache.current.get(target);
    const values = cached ? cached.values : seedEngineValues(target, live);
    const merged = mergeShared(values, sharedValuesOf(live));
    const carriesServer = !cached && isNetworkEngine(live.engine) && isNetworkEngine(target) && String(values.server ?? '').trim() !== '';
    setCarried(carriesServer ? { target, from: live.engine, server: String(values.server ?? '') } : null);
    const restoredMeta = { ...(cached?.meta ?? {}), ...captureSharedMeta(meta) };
    batch(() => {
      form.reset(merged, { keepDefaultValues: true });
      for (const [field, flags] of Object.entries(restoredMeta)) {
        form.setFieldMeta(field as never, (previous) => ({ ...PRISTINE_META, ...previous, ...flags }));
      }
      reconcileFieldMeta(form);
    });
    setLoaded(cached?.loaded ?? null);
  };

  const selectEngine = (target: DatabaseEngine, _source: EngineSource = 'card'): boolean => {
    if (!detached.current && target === form.state.values.engine) return false;
    callbacks.current.onInvalidate({ engineChanged: true });
    switchTo(target);
    return true;
  };

  /** "All engines": the draft is kept, pending work is voided, and the next pick restores or seeds. */
  const leaveToStart = () => {
    if (detached.current) return;
    callbacks.current.onInvalidate({ engineChanged: false });
    captureCurrent();
    detached.current = true;
  };

  const applyPatch = (target: DatabaseEngine, rawPatch: EnginePatch, source: EngineSource) => {
    // A paste, prefill or preset landing on a draft a pick filled: the picker's id no longer describes the target (C3.1).
    const patch = source === 'azure_pick' ? rawPatch : withoutStalePickerProvenance(rawPatch, baseValuesFor(target));
    const engineChanged = detached.current || target !== form.state.values.engine;
    callbacks.current.onInvalidate({ engineChanged });
    if (engineChanged) switchTo(target);
    origins.current.set(target, source);
    batch(() => {
      for (const [field, value] of Object.entries(patch)) form.setFieldValue(field as never, value as never);
      if ('server' in patch) revokeTnsServerProvenance(form);
      reconcileFieldMeta(form);
    });
  };

  /**
   * Round B's delayed password hydration, for the engine the saved password belongs to: into the live form while it is
   * that engine (pristine, empty field only, and never as an edit), else into that engine's cached draft.
   */
  const hydratePassword = (engine: DatabaseEngine, password: string) => {
    if (form.state.values.engine === engine) {
      const dirty = form.state.fieldMeta.password?.isDirty === true;
      if (dirty || form.state.values.password) return;
      callbacks.current.programmatic(() => form.setFieldValue('password', password, { dontUpdateMeta: true }));
      return;
    }
    const draft = cache.current.get(engine);
    if (!draft || draft.meta.password?.isDirty || draft.values.password) return;
    cache.current.set(engine, { ...draft, values: { ...draft.values, password } });
  };

  /** A Load databases answer for `values` (the values it listed with). */
  const adoptLoadedDatabases = (databases: string[], values: Partial<ConnectionFormValues>) =>
    setLoaded({ databases, target: targetFingerprint(values) });

  /**
   * After a save that keeps the dialog open: nothing of any earlier draft may come back. `detach` (create mode) returns
   * to "no engine chosen", so the next pick seeds.
   */
  const clearDrafts = ({ detach = false }: { detach?: boolean } = {}) => {
    setCarried(null);
    cache.current.clear();
    origins.current.clear();
    setLoaded(null);
    if (detach) detached.current = true;
    setCacheVersion((version) => version + 1);
  };

  const visibleDatabases = useMemo(() => (loaded && loaded.target === liveTarget ? loaded.databases : []), [loaded, liveTarget]);

  /**
   * The values a patch for `target` would land on — the live form while it is that engine, else that engine's draft,
   * else its first-visit seed, with the live shared fields (C3: the paste preview's "Still needed"). Reads only.
   */
  const baseValuesFor = (target: DatabaseEngine): ConnectionFormValues => {
    const live = form.state.values;
    if (!detached.current && target === live.engine) return live;
    const cached = cache.current.get(target);
    return mergeShared(cached ? cached.values : seedEngineValues(target, live), sharedValuesOf(live));
  };

  /** The rail's dots. */
  const enginesWithInput = useMemo(
    () => collectEnginesWithInput(cache.current, detached.current, form),
    // `cacheVersion` is the change signal of the ref-held cache and the detached flag; the live meta and engine the rest.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cacheVersion, liveInput, liveEngine],
  );

  return {
    selectEngine,
    restoreEngine: selectEngine,
    applyPatch,
    leaveToStart,
    /** No engine is attached: the dialog shows (or is about to show) the start view. */
    isDetached: () => detached.current,
    hydratePassword,
    adoptLoadedDatabases,
    clearDrafts,
    visibleDatabases,
    enginesWithInput,
    /** Any draft, or a shared field (Name, Color, AI permission), has input: an overlay click must not close. */
    hasInput: liveSharedInput || enginesWithInput.size > 0,
    /** The same, read fresh (C3: an import completing on top of the dialog decides whether it may close it). */
    hasInputNow: () => sharedInput(form.state.fieldMeta as MetaFlags) || collectEnginesWithInput(cache.current, detached.current, form).size > 0,
    changedSince: (baseline: ConnectionFormValues) => changedSinceBaseline(cache.current, detached.current, form, baseline),
    baseValuesFor,
    /** The engine whose draft the host on screen was copied from, while it is untouched (UX-20); else null. */
    serverCarriedFrom: carried && carried.target === liveEngine && carried.server === liveServer && !serverEdited ? carried.from : null,
    /** How `engine`'s draft was filled (`feature:connection_saved.origin`): manual unless a patch filled it. */
    originOf: (engine: DatabaseEngine): SaveOrigin => saveOriginOf(origins.current.get(engine)),
  };
}
